// Shared admin-mockup canvases — extracted from OrderDetail.tsx so the
// Artwork Approval page can render the SAME per-side / per-sheet thumbnails
// + lightbox without duplicating the Fabric.js plumbing.
import React, { useEffect, useRef, useState } from 'react'
import { fabric } from 'fabric'

// Designer canvas dimensions — must match Designer.tsx so a saved spec
// renders pixel-perfect over the garment mockup.
export const GARMENT_CANVAS_W = 640
export const GARMENT_CANVAS_H = 720

// DTF sheets are fixed 57 cm wide × admin-set length. 10 px/cm matches the
// customer DTF builder so spec coordinates land pixel-perfect.
export const DTF_SHEET_W = 570

// ── Shared lightbox modal ────────────────────────────────────────────────
// Click backdrop / Esc / × to close. Locks body scroll while open.
export function MockupLightbox({ open, title, subtitle, eyebrow = 'Customer mockup', onClose, children }: {
  open: boolean
  title: string
  subtitle?: string
  eyebrow?: string
  onClose: () => void
  children: React.ReactNode
}) {
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-3 border-b border-border flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">{eyebrow}</div>
            <h3 className="text-ink font-heading uppercase font-bold text-lg truncate">{title}</h3>
            {subtitle && <div className="text-xs text-ink-3">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-2xl leading-none">×</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
    </div>
  )
}

// ── Garment side mockup ──────────────────────────────────────────────────
// Composites the customer's Fabric canvas on top of the garment mockup at
// the natural 640x720 aspect. No zoom — every surface (thumbnail or
// lightbox) shows the WHOLE garment composite; the container's width is
// what determines the visible footprint. `mode` and `printZone` are kept
// in the signature so existing callers compile; they're ignored.
export function GarmentSideCanvas({ mockupUrl, canvas, apiSrc }: {
  mockupUrl: string | null
  canvas: object | null
  apiSrc: (path: string | null) => string
  mode?: 'thumb' | 'full'
  printZone?: { left: number; top: number; width: number; height: number }
}) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !canvas) return
    const c = new fabric.StaticCanvas(el, {
      width: GARMENT_CANVAS_W, height: GARMENT_CANVAS_H,
      backgroundColor: 'transparent', selection: false, renderOnAddRemove: false,
    })
    try { c.loadFromJSON(canvas, () => { c.requestRenderAll() }) }
    catch (e) { console.warn('side canvas load failed:', (e as Error).message) }
    return () => { c.dispose() }
  }, [canvas])

  return (
    <div className="relative mx-auto bg-surface border border-border rounded overflow-hidden"
         style={{ width: '100%', maxWidth: GARMENT_CANVAS_W, aspectRatio: `${GARMENT_CANVAS_W} / ${GARMENT_CANVAS_H}` }}>
      {mockupUrl && (
        <img src={apiSrc(mockupUrl)} alt="garment mockup"
             className="absolute inset-0 w-full h-full object-contain pointer-events-none" draggable={false} />
      )}
      {canvas && (
        <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none"
                width={GARMENT_CANVAS_W} height={GARMENT_CANVAS_H} />
      )}
    </div>
  )
}

// ── DTF gang-sheet canvas ────────────────────────────────────────────────
// `mode='thumb'` letterboxes the whole sheet inside a 4:5 surface tile so a
// 200 cm sheet doesn't blow out the grid and a 50 cm sheet doesn't look
// squashed — every sheet thumbnail stays a uniform size with the full sheet
// visible. `mode='full'` renders the sheet at its natural max width (used
// by the lightbox and the DTF proof card on OrderDetail).
export interface DtfSheetShape {
  label: string
  lengthCm: number | null
  canvas: object | null
  placements?: Array<{ label: string; qty?: number | null; printWidthMm?: number | null; printHeightMm?: number | null }> | null
}

export function DtfSheetCanvas({ sheet, apiSrc, mode = 'full' }: {
  sheet: DtfSheetShape
  apiSrc: (path: string | null) => string
  mode?: 'thumb' | 'full'
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const SHEET_W = DTF_SHEET_W
  const SHEET_H = (sheet.lengthCm ?? 100) * 10

  useEffect(() => {
    const el = canvasRef.current
    if (!el || !sheet.canvas) return
    const c = new fabric.StaticCanvas(el, {
      width: SHEET_W, height: SHEET_H,
      backgroundColor: '#ffffff', selection: false, renderOnAddRemove: false,
    })
    try {
      c.loadFromJSON(sheet.canvas, () => { c.requestRenderAll() })
    } catch (e) { console.warn('dtf sheet load failed:', (e as Error).message) }
    return () => { c.dispose() }
  }, [sheet.canvas, SHEET_H])

  const sheetBox = (
    <div className="relative bg-white border border-border rounded overflow-hidden"
         style={{ aspectRatio: `${SHEET_W} / ${SHEET_H}`, width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%' }}>
      {sheet.canvas ? (
        <canvas ref={canvasRef} width={SHEET_W} height={SHEET_H} className="w-full h-full block" />
      ) : sheet.placements ? (
        <div className="absolute inset-2 grid gap-1.5"
             style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gridAutoRows: 'minmax(80px, auto)' }}>
          {sheet.placements.map((p, i) => (
            <div key={i} className="border-2 border-dashed border-ink-3 bg-surface flex items-center justify-center text-center p-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-ink-2">
                {p.label}{p.qty && p.qty > 1 ? ` x ${p.qty}` : ''}
                {p.printWidthMm && p.printHeightMm && <div className="text-ink-3 mt-1">{p.printWidthMm}x{p.printHeightMm}mm</div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-ink-3">Empty sheet</div>
      )}
      {void apiSrc /* reserved for per-placement file thumbnails */}
    </div>
  )

  if (mode === 'thumb') {
    return (
      <div>
        <div className="flex items-baseline justify-between mb-2 gap-2">
          <h4 className="text-sm font-bold text-ink truncate">{sheet.label}</h4>
          {sheet.lengthCm && <span className="text-[10px] uppercase tracking-wider text-ink-3 shrink-0">57 x {sheet.lengthCm} cm</span>}
        </div>
        <div className="bg-surface border border-border rounded flex items-center justify-center p-2"
             style={{ aspectRatio: '4 / 5' }}>
          {sheetBox}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-sm font-bold text-ink">{sheet.label}</h4>
        {sheet.lengthCm && <span className="text-xs text-ink-3">57 x {sheet.lengthCm} cm</span>}
      </div>
      <div className="mx-auto" style={{ maxWidth: SHEET_W }}>
        {sheetBox}
      </div>
    </div>
  )
}

// ── Garment side PROOF ───────────────────────────────────────────────────
// Full-page A3 (or whatever the location standard is), transparent canvas,
// the customer's artwork at exact print-zone-relative positions.
//
// Single render path: we bake the proof ONCE at 300 DPI into an offscreen
// Fabric.StaticCanvas, then use that PNG dataURL as BOTH the on-screen
// preview AND the source of the downloaded PNG/PDF. There is literally
// no second render to drift from the first — the preview is the
// downloaded file, scaled down by the browser for display.
//
// Stays byte-identical to OrderDetail.tsx's GarmentSideProofCanvas — both
// surfaces (Order Detail Proof + Artwork Approval View) consume this
// component for that reason.
export function GarmentSideProof({ side, proofBg, orderNumber }: {
  side: {
    name: string
    canvas: object | null
    printZone: { left: number; top: number; width: number; height: number }
    printWidthMm: number
  }
  proofBg: 'white' | 'grey' | 'black'
  orderNumber: number | string
}) {
  const [proofDataUrl, setProofDataUrl] = useState<string | null>(null)
  const [proofPx, setProofPx] = useState<{ w: number; h: number } | null>(null)
  const [rendering, setRendering] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const printWidthMm  = Math.max(1, side.printWidthMm)
  const printHeightMm = printWidthMm * (side.printZone.height / Math.max(1, side.printZone.width))

  // Render once when the side changes. proofBg is preview-only (driven
  // by CSS backdrop below) so it doesn't trigger a re-render.
  useEffect(() => {
    if (!side.canvas) { setProofDataUrl(null); setProofPx(null); return }
    let cancelled = false
    setRendering(true)
    renderProofAt300DPI(side, printWidthMm)
      .then(({ url, w, h }) => {
        if (cancelled) return
        setProofDataUrl(url); setProofPx({ w, h })
      })
      .catch((e) => console.warn('proof render failed:', (e as Error).message))
      .finally(() => { if (!cancelled) setRendering(false) })
    return () => { cancelled = true }
  }, [side, printWidthMm])

  useEffect(() => {
    if (!menuOpen) return
    function onDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  const slug = side.name.toLowerCase().replace(/\s+/g, '-')

  function downloadPng() {
    if (!proofDataUrl) return
    const a = document.createElement('a')
    a.href = proofDataUrl
    a.download = `proof-${orderNumber}-${slug}.png`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    setMenuOpen(false)
  }

  async function downloadPdf() {
    if (!proofDataUrl) return
    setExporting(true)
    try {
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({
        orientation: printWidthMm > printHeightMm ? 'landscape' : 'portrait',
        unit: 'mm', format: [printWidthMm, printHeightMm], compress: true,
      })
      pdf.addImage(proofDataUrl, 'PNG', 0, 0, printWidthMm, printHeightMm)
      pdf.save(`proof-${orderNumber}-${slug}.pdf`)
      setMenuOpen(false)
    } catch (e) { alert('PDF export failed: ' + (e as Error).message) }
    finally { setExporting(false) }
  }

  const DISPLAY_MAX_W = 480
  const aspect = side.printZone.height / Math.max(1, side.printZone.width)
  const widthPx = Math.min(DISPLAY_MAX_W, printWidthMm * 2.2)
  const heightPx = widthPx * aspect

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-left">
          <div className={'text-xs font-bold uppercase tracking-wider ' + (proofBg === 'black' ? 'text-white' : 'text-ink')}>
            {side.name}
          </div>
          <div className={'text-[11px] font-mono mt-0.5 ' + (proofBg === 'black' ? 'text-white/60' : 'text-ink-3')}>
            {printWidthMm.toFixed(0)} x {printHeightMm.toFixed(0)} mm · transparent{rendering ? ' · rendering…' : ''}
          </div>
        </div>
        <div ref={menuRef} className="relative">
          <button onClick={() => setMenuOpen((o) => !o)} disabled={exporting || !proofDataUrl}
            aria-haspopup="menu" aria-expanded={menuOpen}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded bg-ink text-white hover:bg-ink-2 transition-colors whitespace-nowrap disabled:opacity-60">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exporting ? 'Exporting…' : 'Download proof'}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {menuOpen && (
            <div role="menu"
              className="absolute right-0 top-[calc(100%+4px)] z-30 bg-white border border-border rounded shadow-lg overflow-hidden min-w-[170px]"
              onMouseDown={(e) => e.stopPropagation()}>
              <button role="menuitem" onClick={downloadPng} disabled={!proofDataUrl}
                className="flex items-center justify-between w-full text-left px-3 py-2 text-xs hover:bg-surface gap-3 disabled:opacity-60">
                <span><strong className="text-ink">PNG</strong> 300 DPI</span>
                <span className="text-[10px] uppercase tracking-wider text-ink-3 font-mono">
                  {proofPx ? `${proofPx.w}x${proofPx.h}px` : '…'}
                </span>
              </button>
              <button role="menuitem" onClick={downloadPdf} disabled={exporting || !proofDataUrl}
                className="flex items-center justify-between w-full text-left px-3 py-2 text-xs hover:bg-surface gap-3 border-t border-border disabled:opacity-60">
                <span><strong className="text-ink">PDF</strong> press-ready</span>
                <span className="text-[10px] uppercase tracking-wider text-ink-3 font-mono">
                  {printWidthMm.toFixed(0)}x{printHeightMm.toFixed(0)}mm
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="mx-auto" style={{ width: widthPx }}>
        {/* The proof box IS the print area — no padding, margin, or
            border bleeding into its dimensions. The preview displays the
            SAME PNG that gets downloaded (scaled by the browser to the
            display size). No second render path = no positional drift. */}
        <div className="relative overflow-hidden"
             style={{
               width: widthPx,
               height: heightPx,
               boxShadow: '0 0 0 1px var(--border, rgba(0,0,0,0.1))',
               background: proofBg === 'black'
                 ? '#0C0D0F'
                 : proofBg === 'grey'
                   ? '#9CA3AF'
                   : 'conic-gradient(at 50% 50%, #ECECEC 25%, #FAFAFA 0 50%, #ECECEC 0 75%, #FAFAFA 0)',
               backgroundSize: proofBg === 'white' ? '16px 16px' : undefined,
             }}>
          {proofDataUrl ? (
            <img src={proofDataUrl} alt={`${side.name} proof`}
                 className="absolute inset-0 w-full h-full block"
                 style={{ objectFit: 'fill' }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-ink-3">
              {rendering ? 'Rendering proof…' : 'No canvas data'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Render the proof at 300 DPI using Fabric.js's idiomatic sub-rect +
// multiplier export. Used by GarmentSideProof so the preview and the
// download share ONE bake; also exported so the order-zip bundler can
// emit the same bytes into the ZIP without a second render path.
//
// The customer's saved fabric JSON is loaded into a single StaticCanvas at
// the Designer's native size (640×720). All objects retain their original
// canvas coordinates. We then ask Fabric to emit just the print-zone
// rectangle at a `multiplier` chosen so the output lands at exactly 300
// DPI relative to the print zone's mm width. ONE function call replaces
// the previous setViewportTransform + renderAll + toDataURL dance — the
// whole sub-rect crop + scale is handled by Fabric itself.
export async function renderProofAt300DPI(
  side: {
    canvas: object | null
    printZone: { left: number; top: number; width: number; height: number }
  },
  printWidthMm: number,
): Promise<{ url: string; w: number; h: number }> {
  if (!side.canvas) throw new Error('No canvas data for this side')
  const tmpEl = document.createElement('canvas')
  tmpEl.width = GARMENT_CANVAS_W
  tmpEl.height = GARMENT_CANVAS_H
  const c = new fabric.StaticCanvas(tmpEl, {
    width: GARMENT_CANVAS_W, height: GARMENT_CANVAS_H,
    backgroundColor: '',
    enableRetinaScaling: false, renderOnAddRemove: false,
  })
  try {
    await new Promise<void>((resolve) => {
      c.loadFromJSON(side.canvas, () => {
        c.renderAll()
        resolve()
      })
    })
    // Multiplier = output px per canvas px so that the print-zone width
    // in canvas-px scales to printWidthMm × 300/25.4 px in the output.
    const PX_PER_MM_AT_300_DPI = 300 / 25.4
    const multiplier = (printWidthMm * PX_PER_MM_AT_300_DPI) / Math.max(1, side.printZone.width)
    const url = c.toDataURL({
      format: 'png',
      left:   side.printZone.left,
      top:    side.printZone.top,
      width:  side.printZone.width,
      height: side.printZone.height,
      multiplier,
    })
    const w = Math.round(side.printZone.width  * multiplier)
    const h = Math.round(side.printZone.height * multiplier)
    return { url, w, h }
  } finally {
    c.dispose()
  }
}

// ── DTF sheet PROOF ──────────────────────────────────────────────────────
// One DTF sheet rendered for production — same fabric JSON, white or black
// substrate per the proofBg toggle, plus a PNG/PDF download split button.
// Mirrors OrderDetail.tsx's DtfSheetProofCanvas (which the DTF Proof card
// keeps using directly). Reused here so Artwork Approval's View on a DTF
// piece shows EXACTLY the same proof admin sees on Order Detail.
export function DtfSheetProof({ sheet, proofBg, orderNumber }: {
  sheet: {
    label: string
    canvas: object | null
    lengthCm: number | null
  }
  proofBg: 'white' | 'grey' | 'black'
  orderNumber: number | string
}) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  const SHEET_W = DTF_SHEET_W
  const SHEET_H = (sheet.lengthCm ?? 100) * 10
  const [menuOpen, setMenuOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !sheet.canvas) return
    // Transparent canvas — DTF transfers are printed on clear film so the
    // press file MUST carry alpha (the printer lays down a white-ink
    // underlay only where the artwork pixels are opaque). Substrate
    // colour shown in the preview is a CSS backdrop, not part of the
    // exported PNG.
    const c = new fabric.StaticCanvas(el, {
      width: SHEET_W, height: SHEET_H,
      backgroundColor: '',
      selection: false, renderOnAddRemove: false,
    })
    try {
      c.loadFromJSON(sheet.canvas, () => {
        // loadFromJSON restores backgroundColor from the spec, so older
        // saves with `#ffffff` baked in would still bleed through.
        // Force transparent after the load completes.
        c.setBackgroundColor('', () => {})
        c.requestRenderAll()
      })
    }
    catch (e) { console.warn('dtf proof sheet load failed:', (e as Error).message) }
    return () => { c.dispose() }
  }, [sheet.canvas, SHEET_H])

  useEffect(() => {
    if (!menuOpen) return
    function onDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  const slug = sheet.label.toLowerCase().replace(/\s+/g, '-')

  function downloadPng() {
    const el = ref.current; if (!el) return
    try {
      const url = el.toDataURL('image/png')
      const a = document.createElement('a')
      a.href = url
      a.download = `proof-${orderNumber}-${slug}.png`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setMenuOpen(false)
    } catch (e) { alert('Download failed: ' + (e as Error).message) }
  }

  async function downloadPdf() {
    const el = ref.current; if (!el) return
    setExporting(true)
    try {
      const url = el.toDataURL('image/png')
      const { jsPDF } = await import('jspdf')
      const widthCm  = 57
      const heightCm = sheet.lengthCm ?? 100
      const pdf = new jsPDF({
        orientation: widthCm > heightCm ? 'landscape' : 'portrait',
        unit: 'cm',
        format: [widthCm, heightCm],
        compress: true,
      })
      pdf.addImage(url, 'PNG', 0, 0, widthCm, heightCm)
      pdf.save(`proof-${orderNumber}-${slug}.pdf`)
      setMenuOpen(false)
    } catch (e) { alert('PDF export failed: ' + (e as Error).message) }
    finally { setExporting(false) }
  }

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-sm font-bold text-ink">{sheet.label}</h4>
        <div className="flex items-center gap-3">
          {sheet.lengthCm && <span className="text-xs text-ink-3">57 x {sheet.lengthCm} cm</span>}
          <div ref={menuRef} className="relative">
            <button onClick={() => setMenuOpen((o) => !o)} disabled={exporting}
              aria-haspopup="menu" aria-expanded={menuOpen}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded bg-ink text-white hover:bg-ink-2 transition-colors whitespace-nowrap disabled:opacity-60">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {exporting ? 'Exporting…' : 'Download sheet'}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" className="opacity-70">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {menuOpen && (
              <div role="menu"
                className="absolute right-0 top-[calc(100%+4px)] z-30 bg-white border border-border rounded shadow-lg overflow-hidden min-w-[160px]"
                onMouseDown={(e) => e.stopPropagation()}>
                <button role="menuitem" onClick={downloadPng}
                  className="flex items-center justify-between w-full text-left px-3 py-2 text-xs hover:bg-surface gap-3">
                  <span><strong className="text-ink">PNG</strong> bitmap</span>
                  <span className="text-[10px] uppercase tracking-wider text-ink-3">{SHEET_W}x{SHEET_H}px</span>
                </button>
                <button role="menuitem" onClick={downloadPdf} disabled={exporting}
                  className="flex items-center justify-between w-full text-left px-3 py-2 text-xs hover:bg-surface gap-3 border-t border-border disabled:opacity-60">
                  <span><strong className="text-ink">PDF</strong> press-ready</span>
                  <span className="text-[10px] uppercase tracking-wider text-ink-3">57x{sheet.lengthCm ?? 100}cm</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="relative mx-auto border border-border rounded overflow-hidden"
           style={{
             width: '100%', maxWidth: SHEET_W, aspectRatio: `${SHEET_W} / ${SHEET_H}`,
             background: proofBg === 'black'
               ? '#0C0D0F'
               : proofBg === 'grey'
                 ? '#9CA3AF'
                 // White mode shows a subtle checker so the admin can SEE that
                 // the sheet's transparent regions are actually transparent
                 // (not just white-filled). Backdrop is preview-only; the
                 // exported PNG always carries full alpha.
                 : 'conic-gradient(at 50% 50%, #ECECEC 25%, #FAFAFA 0 50%, #ECECEC 0 75%, #FAFAFA 0)',
             backgroundSize: proofBg === 'white' ? '16px 16px' : undefined,
           }}>
        <canvas ref={ref} width={SHEET_W} height={SHEET_H} className="w-full h-full block" />
      </div>
    </div>
  )
}

// Bake one DTF gang-sheet to a TRANSPARENT PNG dataURL via Fabric's
// standard render. The canvas stays transparent (DTF transfers are
// printed on clear film, so the press file must carry alpha — the
// printer uses the alpha channel to drive the white-ink underlay
// underneath the colour pass). The proofBg argument is kept on the
// signature for API stability but is no longer baked into the file.
export async function renderDtfSheetProof(
  sheet: { canvas: object | null; lengthCm: number | null },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _proofBg: 'white' | 'grey' | 'black',
): Promise<{ url: string; w: number; h: number }> {
  if (!sheet.canvas) throw new Error('No canvas data for this sheet')
  const W = DTF_SHEET_W
  const H = (sheet.lengthCm ?? 100) * 10
  const tmpEl = document.createElement('canvas')
  tmpEl.width = W
  tmpEl.height = H
  const c = new fabric.StaticCanvas(tmpEl, {
    width: W, height: H,
    backgroundColor: '',
    enableRetinaScaling: false, renderOnAddRemove: false,
  })
  try {
    await new Promise<void>((resolve) => {
      c.loadFromJSON(sheet.canvas, () => {
        // Force transparent again — loadFromJSON restores the spec's
        // backgroundColor, so older saves with `#ffffff` baked in would
        // otherwise produce an opaque PNG and defeat the white-ink
        // underlay at press.
        c.setBackgroundColor('', () => {})
        c.renderAll()
        resolve()
      })
    })
    return { url: c.toDataURL({ format: 'png', multiplier: 1 }), w: W, h: H }
  } finally {
    c.dispose()
  }
}
